import type { ContactWay, IndustryDef, Potential, Status } from '../types';

export const STATUS: Record<Status, { label: string }> = {
  uusi: { label: 'Uusi' },
  kontaktoitu: { label: 'Kontaktoitu' },
  tarjous: { label: 'Tarjous' },
  kauppa: { label: 'Kauppa' },
  ei_kiinnostunut: { label: 'Ei kiinnostunut' },
};

/** Työjonon sarakkeet, joihin tilanne kirjataan. Uusi on lähtötila, ei merkintä. */
export const KIRJAUS_VAIHEET: Status[] = ['kontaktoitu', 'tarjous', 'kauppa'];

/** Toimialojen nimet tulevat palvelimelta; tämä rakentaa niistä hakutaulun.
 *  Koodissa ei ole yhdenkään asiakkaan toimialoja. */
export const industryMap = (lista: IndustryDef[]): Record<string, string> =>
  Object.fromEntries((lista || []).map(t => [t.key, t.label]));

/** Nimi tunnuksesta, kun listaa ei ole käsillä (tunnus on aina parempi kuin tyhjä). */
export const industryLabel = (lista: IndustryDef[], key?: string | null): string =>
  (key && (lista || []).find(t => t.key === key)?.label) || key || '';

// Yksi pallo musta, kaksi oranssi, kolme vihreä. Sama väri kaikissa näkymissä.
export const POTENTIAL: Record<Potential, { label: string; color: string; merkki: string; selitys: string }> = {
  matala: { label: 'Matala', color: '#1A1A1A', merkki: '●○○', selitys: 'pieni tai sopivuus epävarma' },
  keskitaso: { label: 'Keskitaso', color: '#B85A0C', merkki: '●●○', selitys: 'sopiva, koko tai ajoitus ei erotu' },
  korkea: { label: 'Korkea', color: '#1A8F46', merkki: '●●●', selitys: 'selvä tarve juuri nyt' },
};

/** Selitteen järjestys: yksi, kaksi, kolme palloa. */
export const POTENTIAALI_NAKYMA: Potential[] = ['matala', 'keskitaso', 'korkea'];

const POTENTIAL_JARJESTYS: Record<Potential, number> = { korkea: 0, keskitaso: 1, matala: 2 };
/** Arvioimaton viimeiseksi, jottei se peitä parhaita kohteita. */
export const potJarjestys = (p: Potential | null | undefined): number =>
  (p && POTENTIAL_JARJESTYS[p]) ?? 3;

export const CONTACT_WAY: Record<ContactWay, string> = {
  sahkoposti: '📧 Sähköposti',
  lomake: '📝 Lomake',
  puhelu: '📞 Puhelu',
};

export const EVENT_LABEL: Record<string, string> = {
  sahkoposti: '📧 Sähköposti lähetetty',
  lomake: '📝 Lomake lähetetty',
  puhelu: '📞 Puhelu',
  kontaktoitu: 'Kontaktoitu',
  vastasi: 'Vastasi',
  tapaaminen: 'Tapaaminen',
  tarjous: '📄 Tarjous lähetetty',
  kauppa: '✅ Kauppa kirjattu',
  ei_kiinnostunut: 'Hylätty',
  muistiinpano: '🗒 Muistiinpano',
  status: '🔁 Status',
};

export const EXCLUSION_REASON: Record<string, string> = {
  poistettu: 'Poistettu liidi',
  ei_kiinnostunut: 'Ei kiinnostunut',
  ei_kiinnosta: 'Ohitettu AI-haussa',
};

export const KIND_FI: Record<string, string> = {
  viesti: 'Viestiluonnokset',
  liidihaku: 'AI-liidihaku',
};

export const KUUKAUDET = ['tammikuu', 'helmikuu', 'maaliskuu', 'huhtikuu', 'toukokuu', 'kesäkuu',
  'heinäkuu', 'elokuu', 'syyskuu', 'lokakuu', 'marraskuu', 'joulukuu'];
export const VIIKONPAIVAT = ['ma', 'ti', 'ke', 'to', 'pe', 'la', 'su'];

export const MAAT_OLETUS = ['Suomi', 'Ruotsi', 'Norja', 'Tanska', 'Viro'];

/**
 * Laskutustiedot, jotka asiakas täyttää itse. Tallentuvat settings-tauluun
 * laskutus_*-avaimilla, ja master-hallinta lukee ne sieltä laskutusta varten.
 * Sama lista on master/src/masterdb.js:ssä — pidä ne synkassa.
 */
export const LASKUTUS_KENTAT: {
  key: string; label: string; pakollinen?: boolean; hint?: string; leveys?: 'puoli' | 'koko';
}[] = [
  { key: 'laskutus_nimi', label: 'Laskutusnimi', pakollinen: true, hint: 'Jos eri kuin yrityksen nimi' },
  { key: 'laskutus_ytunnus', label: 'Y-tunnus', pakollinen: true, hint: '1234567-8' },
  { key: 'laskutus_alv', label: 'ALV-tunnus', hint: 'esim. FI12345678 (EU-laskutus)' },
  { key: 'laskutus_osoite', label: 'Laskutusosoite', pakollinen: true, leveys: 'koko' },
  { key: 'laskutus_postinumero', label: 'Postinumero', pakollinen: true },
  { key: 'laskutus_kaupunki', label: 'Postitoimipaikka', pakollinen: true },
  { key: 'laskutus_maa', label: 'Maa' },
  { key: 'laskutus_verkkolasku', label: 'Verkkolaskuosoite (OVT)', hint: 'Ensisijainen toimitustapa' },
  { key: 'laskutus_operaattori', label: 'Välittäjätunnus', hint: 'esim. 003721291126' },
  { key: 'laskutus_email', label: 'Laskutussähköposti', hint: 'Jos verkkolaskuosoitetta ei ole' },
  { key: 'laskutus_viite', label: 'Viitteenne / kustannuspaikka', hint: 'Tulostuu laskulle' },
  { key: 'laskutus_yhteyshenkilo', label: 'Laskujen yhteyshenkilö' },
  { key: 'laskutus_puhelin', label: 'Puhelin' },
];

/** Laskutustietolomakkeen puutteet. Kortti ei ole tämän lomakkeen kenttä. */
export function laskutusPuutteet(arvot: Record<string, string | undefined>): string[] {
  const puuttuu = LASKUTUS_KENTAT
    .filter(k => k.pakollinen && !String(arvot[k.key] || '').trim())
    .map(k => k.label);
  const tapa = arvot.maksutapa || 'lasku';
  if (tapa !== 'kortti'
      && !String(arvot.laskutus_verkkolasku || '').trim()
      && !String(arvot.laskutus_email || '').trim()) {
    puuttuu.push('Verkkolaskuosoite tai laskutussähköposti');
  }
  return puuttuu;
}

/**
 * Yritysprofiili: asiakkaan oma kuvaus liiketoiminnastaan. AI saa tietonsa
 * vain täältä — samassa koodissa ei ole yhdenkään asiakkaan tuotteita.
 * Sama lista on src/templates.js:ssä, pidä ne synkassa.
 */
export const YRITYSPROFIILI: {
  key: string; label: string; pakollinen?: boolean; rivit: number; hint: string;
}[] = [
  { key: 'yritys_kuvaus', label: 'Mitä yritys tekee', pakollinen: true, rivit: 3,
    hint: 'Yhdellä kappaleella: toimiala, koko, millä markkinalla toimitte.' },
  { key: 'yritys_tuotteet', label: 'Tuotteet tai palvelut', pakollinen: true, rivit: 4,
    hint: 'Mitä myytte ja mikä on kunkin ydinhyöty asiakkaalle.' },
  { key: 'yritys_kohdeasiakkaat', label: 'Ihanteellinen asiakas', pakollinen: true, rivit: 3,
    hint: 'Millainen organisaatio hyötyy eniten: toimiala, koko, tilanne. Tätä AI käyttää liidihaussa.' },
  { key: 'yritys_myyntiargumentit', label: 'Tärkeimmät myyntiargumentit', rivit: 4,
    hint: 'Konkreettiset hyödyt ja luvut, joita saa käyttää viesteissä.' },
  { key: 'yritys_referenssit', label: 'Referenssit ja todisteet', rivit: 3,
    hint: 'Asiakastarinat, sertifikaatit tai luvut, joihin voi viitata.' },
  { key: 'yritys_tavoitteet', label: 'Mitä myynnillä tavoitellaan', rivit: 3,
    hint: 'Esim. kauppojen koko, halutut toimialat, kasvutavoite tai uusi markkina.' },
  { key: 'yritys_savy', label: 'Viestien sävy', rivit: 2,
    hint: 'Esim. asiallinen ja lämmin, ei ylisanoja, teitittely vai sinuttelu.' },
  { key: 'yritys_valta', label: 'Mitä AI ei saa tehdä', rivit: 2,
    hint: 'Kiellettyjä lupauksia, sanoja tai aiheita.' },
];

/** AI ei kirjoita mitään ennen kuin nämä on täytetty. */
export function profiiliPuutteet(s: Record<string, string | undefined>): string[] {
  return YRITYSPROFIILI
    .filter(k => k.pakollinen && !String(s[k.key] || '').trim())
    .map(k => k.label);
}

export const TABS = [
  ['tanaan', 'Tänään'],
  ['putki', 'Työjono'],
  ['kalenteri', 'Kalenteri'],
  ['haku', 'Hae liidejä'],
  ['liidit', 'Kaikki liidit'],
  ['tuonti', 'Tuo liidejä'],
  ['pohjat', 'Viestipohjat'],
  ['profiili', 'Yritysprofiili'],
  ['raportointi', 'Raportointi'],
  ['laskutus', 'Laskutus'],
  ['asetukset', 'Asetukset'],
] as const;

export type TabId = (typeof TABS)[number][0];

/** Rekisteri ja tiedostotuonti ovat Liidit-otsikon alla. Haku on oma linkki. */
export const LIIDIT_RYHMA: TabId[] = ['liidit', 'tuonti'];
